import { useCallback } from "react";
import { Button, Checkbox, Collapse, DatePicker, Input, InputNumber, Select, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
    ACORN_QUOTE_CATEGORIES,
    ACORN_QUOTE_CURRENCIES,
    createAcornEmptyLine,
} from "./acornQuoteData";
import { calcAcornLineSubtotal, formatAcornMoney } from "./acornQuoteFormat";

const { Paragraph, Title } = Typography;

function AcornLineItem({ line, currency, canRemove, t, onChange, onRemove }) {
    return (
        <div className="acorn-line-item">
            <div className="acorn-line-item__grid">
                <div className="acorn-line-item__field">
                    <label>{t("form.itemName")}</label>
                    <Input
                        value={line.name}
                        placeholder={t("form.itemNamePlaceholder")}
                        onChange={(event) => onChange({ ...line, name: event.target.value })}
                    />
                </div>
                <div className="acorn-line-item__field">
                    <label>{t("form.unitPrice")}</label>
                    <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.unitPrice}
                        placeholder="0.00"
                        onChange={(event) => onChange({ ...line, unitPrice: event.target.value })}
                    />
                </div>
                <div className="acorn-line-item__field">
                    <label>{t("form.quantity")}</label>
                    <InputNumber
                        min={0}
                        value={line.quantity}
                        onChange={(value) => onChange({ ...line, quantity: value == null ? 0 : value })}
                        style={{ width: "100%" }}
                    />
                </div>
                <div className="acorn-line-item__field">
                    <label>{t("form.subtotal")}</label>
                    <div className="acorn-line-item__subtotal">
                        {formatAcornMoney(calcAcornLineSubtotal(line.unitPrice, line.quantity), currency)}
                    </div>
                </div>
            </div>

            <div className="acorn-line-item__note-row">
                <div className="acorn-line-item__field">
                    <label>{t("form.note")}</label>
                    <Input
                        value={line.note}
                        placeholder={t("form.notePlaceholder")}
                        onChange={(event) => onChange({ ...line, note: event.target.value })}
                    />
                </div>
                {canRemove && (
                    <Button type="text" danger onClick={onRemove}>
                        {t("form.removeLine")}
                    </Button>
                )}
            </div>
        </div>
    );
}

function AcornQuoteForm({ formState, onChange, validationError, t }) {
    const updateField = useCallback(
        (field, value) => {
            onChange({ ...formState, [field]: value });
        },
        [formState, onChange]
    );

    const updateCategoryLines = useCallback(
        (categoryKey, lines) => {
            onChange({
                ...formState,
                categories: {
                    ...formState.categories,
                    [categoryKey]: lines,
                },
            });
        },
        [formState, onChange]
    );

    const handleAddLine = useCallback(
        (categoryKey) => {
            const lines = formState.categories[categoryKey] || [];
            updateCategoryLines(categoryKey, [...lines, createAcornEmptyLine()]);
        },
        [formState.categories, updateCategoryLines]
    );

    const handleUpdateLine = useCallback(
        (categoryKey, lineId, nextLine) => {
            const lines = (formState.categories[categoryKey] || []).map((line) =>
                line.id === lineId ? nextLine : line
            );
            updateCategoryLines(categoryKey, lines);
        },
        [formState.categories, updateCategoryLines]
    );

    const handleRemoveLine = useCallback(
        (categoryKey, lineId) => {
            const lines = formState.categories[categoryKey] || [];
            if (lines.length <= 1) {
                return;
            }
            updateCategoryLines(
                categoryKey,
                lines.filter((line) => line.id !== lineId)
            );
        },
        [formState.categories, updateCategoryLines]
    );

    const handleGlobalNoteChange = useCallback(
        (index, value) => {
            const nextNotes = [...formState.globalNotes];
            nextNotes[index] = value;
            updateField("globalNotes", nextNotes);
        },
        [formState.globalNotes, updateField]
    );

    const handleAddGlobalNote = useCallback(() => {
        updateField("globalNotes", [...formState.globalNotes, ""]);
    }, [formState.globalNotes, updateField]);

    const handleRemoveGlobalNote = useCallback(
        (index) => {
            if (formState.globalNotes.length <= 1) {
                return;
            }
            updateField(
                "globalNotes",
                formState.globalNotes.filter((_, noteIndex) => noteIndex !== index)
            );
        },
        [formState.globalNotes, updateField]
    );

    const categorySubtotals = ACORN_QUOTE_CATEGORIES.map((key) => {
        const lines = formState.categories[key] || [];
        const subtotal = lines.reduce(
            (sum, line) => sum + calcAcornLineSubtotal(line.unitPrice, line.quantity),
            0
        );
        return { key, subtotal };
    });

    const grandTotal = categorySubtotals.reduce((sum, category) => sum + category.subtotal, 0);

    const collapseItems = ACORN_QUOTE_CATEGORIES.map((categoryKey, index) => {
        const lines = formState.categories[categoryKey] || [];
        const categorySubtotal =
            categorySubtotals.find((category) => category.key === categoryKey)?.subtotal || 0;

        return {
            key: categoryKey,
            label: (
                <div className="acorn-form__collapse-label">
                    <span>
                        {index + 1}. {t(`categories.${categoryKey}`)}
                    </span>
                    <span className="acorn-form__collapse-subtotal">
                        {formatAcornMoney(categorySubtotal, formState.currency)}
                    </span>
                </div>
            ),
            children: (
                <div className="acorn-form__category">
                    {lines.map((line) => (
                        <AcornLineItem
                            key={line.id}
                            line={line}
                            currency={formState.currency}
                            canRemove={lines.length > 1}
                            t={t}
                            onChange={(nextLine) => handleUpdateLine(categoryKey, line.id, nextLine)}
                            onRemove={() => handleRemoveLine(categoryKey, line.id)}
                        />
                    ))}
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => handleAddLine(categoryKey)}
                        className="acorn-form__add-line"
                    >
                        {t("form.addLine")}
                    </Button>
                </div>
            ),
        };
    });

    return (
        <div className="acorn-form">
            <section className="acorn-form__section">
                <Title level={4}>{t("form.basicInfo")}</Title>

                <div className="acorn-form__field">
                    <label htmlFor="acorn-project-name">{t("form.projectName")}</label>
                    <Input
                        id="acorn-project-name"
                        value={formState.projectName}
                        placeholder={t("form.projectNamePlaceholder")}
                        onChange={(event) => updateField("projectName", event.target.value)}
                    />
                </div>

                <div className="acorn-form__row">
                    <div className="acorn-form__field">
                        <label>{t("form.quoteDate")}</label>
                        <DatePicker
                            value={formState.quoteDate}
                            onChange={(value) => updateField("quoteDate", value)}
                            format="YYYY-MM-DD"
                            style={{ width: "100%" }}
                            inputReadOnly
                        />
                    </div>
                    <div className="acorn-form__field">
                        <label>{t("form.validUntil")}</label>
                        <DatePicker
                            value={formState.validUntil}
                            onChange={(value) => updateField("validUntil", value)}
                            format="YYYY-MM-DD"
                            style={{ width: "100%" }}
                            inputReadOnly
                            allowClear
                        />
                    </div>
                </div>

                <div className="acorn-form__row">
                    <div className="acorn-form__field">
                        <label>{t("form.currency")}</label>
                        <Select
                            value={formState.currency}
                            onChange={(value) => updateField("currency", value)}
                            options={ACORN_QUOTE_CURRENCIES.map((value) => ({
                                value,
                                label: value,
                            }))}
                            style={{ width: "100%" }}
                        />
                    </div>
                    <div className="acorn-form__field">
                        <label htmlFor="acorn-portfolio-url">{t("form.portfolioUrl")}</label>
                        <Input
                            id="acorn-portfolio-url"
                            value={formState.portfolioUrl}
                            placeholder={t("form.portfolioUrlPlaceholder")}
                            onChange={(event) => updateField("portfolioUrl", event.target.value)}
                        />
                    </div>
                </div>

                <div className="acorn-form__field acorn-form__field--checkbox">
                    <Checkbox
                        checked={formState.hideSubitemPrices}
                        onChange={(event) => updateField("hideSubitemPrices", event.target.checked)}
                    >
                        {t("form.hideSubitemPrices")}
                    </Checkbox>
                </div>
            </section>

            <section className="acorn-form__section">
                <Title level={4}>{t("form.lineItems")}</Title>
                <Collapse items={collapseItems} defaultActiveKey={ACORN_QUOTE_CATEGORIES} />
            </section>

            <section className="acorn-form__section">
                <Title level={4}>{t("form.globalNotes")}</Title>
                <Paragraph type="secondary" className="acorn-form__hint">
                    {t("form.globalNotesHint")}
                </Paragraph>

                <div className="acorn-form__notes">
                    {formState.globalNotes.map((note, index) => (
                        <div key={`acorn-note-${index}`} className="acorn-form__note-row">
                            <Input
                                value={note}
                                placeholder={t("form.globalNotePlaceholder")}
                                onChange={(event) => handleGlobalNoteChange(index, event.target.value)}
                            />
                            {formState.globalNotes.length > 1 && (
                                <Button type="text" danger onClick={() => handleRemoveGlobalNote(index)}>
                                    {t("form.removeNote")}
                                </Button>
                            )}
                            {index === formState.globalNotes.length - 1 && (
                                <Button
                                    type="text"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddGlobalNote}
                                    aria-label={t("form.addNote")}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </section>

            <div className="acorn-form__total-bar">
                <span>{t("form.grandTotal")}</span>
                <strong>{formatAcornMoney(grandTotal, formState.currency)}</strong>
            </div>

            {validationError && (
                <Paragraph type="danger" className="acorn-form__error">
                    {validationError}
                </Paragraph>
            )}
        </div>
    );
}

export default AcornQuoteForm;
